(() => {
  if (customElements.get('rr-shop-scene')) return;

  class RRShopScene extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.initializing) return;
      this.initializing = true;
      requestAnimationFrame(() => {
        this.initializing = false;
        this.initialize();
      });
    }

    initialize() {
      this.track = this.querySelector('[data-scene-track]');
      this.scenes = Array.from(this.querySelectorAll('[data-scene]'));
      this.current = this.querySelector('[data-scene-current]');
      this.previous = this.querySelector('[data-scene-previous]');
      this.next = this.querySelector('[data-scene-next]');
      this.mobileQuery = window.matchMedia('(max-width: 989px)');

      if (!this.track || !this.scenes.length || this.initialized) return;
      this.initialized = true;

      this.previous?.addEventListener('click', () => this.move(-1));
      this.next?.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', () => this.handleScroll(), { passive: true });

      this.addEventListener('click', (event) => {
        const pin = event.target.closest('[data-product-pin]');
        if (pin) {
          this.openProduct(pin.closest('[data-scene]'), pin.dataset.productPin);
          return;
        }

        const close = event.target.closest('[data-product-close]');
        if (close) this.closeProduct(close.closest('[data-scene]'), true);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openScene = this.querySelector('[data-scene].is-product-open');
        if (openScene) this.closeProduct(openScene, true);
      });

      this.mobileQuery.addEventListener?.('change', () => this.syncLayout());
      this.syncLayout();
      this.updateNavigation(0);
    }

    move(direction) {
      const active = this.activeSceneIndex();
      const destination = Math.max(0, Math.min(this.scenes.length - 1, active + direction));
      const destinationLeft = this.scenes[destination].offsetLeft - this.track.offsetLeft;
      this.track.scrollTo({ left: destinationLeft, behavior: 'smooth' });
      this.updateNavigation(destination);
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.updateNavigation(this.activeSceneIndex());
        this.scrollFrame = null;
      });
    }

    activeSceneIndex() {
      const trackLeft = this.track.scrollLeft + this.track.offsetLeft;
      return this.scenes.reduce((closestIndex, scene, index) => {
        const currentDistance = Math.abs(scene.offsetLeft - trackLeft);
        const closestDistance = Math.abs(this.scenes[closestIndex].offsetLeft - trackLeft);
        return currentDistance < closestDistance ? index : closestIndex;
      }, 0);
    }

    updateNavigation(index) {
      const active = Math.max(0, Math.min(this.scenes.length - 1, index));
      if (this.current) this.current.textContent = String(active + 1).padStart(2, '0');
      if (this.previous) this.previous.disabled = active === 0;
      if (this.next) this.next.disabled = active === this.scenes.length - 1;
    }

    openProduct(scene, productId) {
      if (!scene) return;
      scene.querySelectorAll('[data-product-pin]').forEach((pin) => {
        pin.setAttribute('aria-expanded', String(pin.dataset.productPin === productId));
      });
      scene.querySelectorAll('[data-product-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.productPanel !== productId;
      });
      scene.classList.add('is-product-open');

      if (this.mobileQuery.matches) {
        const panel = scene.querySelector(`[data-product-panel="${CSS.escape(productId)}"]`);
        window.setTimeout(() => panel?.focus({ preventScroll: true }), 180);
      }
    }

    closeProduct(scene, returnFocus) {
      if (!scene) return;
      const selected = scene.querySelector('[data-product-pin][aria-expanded="true"]');
      scene.classList.remove('is-product-open');
      scene.querySelectorAll('[data-product-panel]').forEach((panel) => { panel.hidden = true; });
      scene.querySelectorAll('[data-product-pin]').forEach((pin) => pin.setAttribute('aria-expanded', 'false'));
      if (returnFocus) selected?.focus({ preventScroll: true });
    }

    syncLayout() {
      this.scenes.forEach((scene) => {
        const firstPin = scene.querySelector('[data-product-pin]');
        if (this.mobileQuery.matches) {
          this.closeProduct(scene, false);
        } else if (firstPin) {
          const selected = scene.querySelector('[data-product-pin][aria-expanded="true"]') || firstPin;
          this.openProduct(scene, selected.dataset.productPin);
        }
      });
    }
  }

  customElements.define('rr-shop-scene', RRShopScene);
})();
